<!-- CarMedical Record -->
<template>
  <div class="cr-list-page">
    <!-- 상단 검색 영역 -->
    <div class="search-area">
      <div class="search-row">
        <select v-model="searchMode" class="search-mode-select">
          <option value="receipt">영수증 조회</option>
          <option value="customer">고객 조회</option>
        </select>
        <!-- 영수증 조회 -->
        <template v-if="searchMode === 'receipt'">
          <div class="info-label">본부</div>
          <select
            v-model="selectedBranch"
            class="branch-select"
            :disabled="userInfo && userInfo.OGN_FG === '01'"
          >
            <option
              v-for="branch in branchList"
              :key="branch.BRANCH_CD"
              :value="branch.BRANCH_CD"
            >
              {{ branch.BRANCH_NM }}
            </option>
          </select>
          <div class="info-label">매장명</div>
          <input
            list="team-datalist"
            v-model="teamSearch"
            @change="handleTeamSelection"
            class="team-select"
            placeholder="매장 검색 또는 선택"
            :disabled="userInfo && userInfo.OGN_FG === '01'"
          />
          <datalist id="team-datalist">
            <option
              v-for="team in teamList"
              :key="team.SHOP_CD"
              :value="`(${team.VAN_BIZ_NO}) ${team.SHOP_NM}`"
            />
          </datalist>
          <input
            type="date"
            v-model="searchDate"
            class="date-picker"
            @blur="validateDate"
            min="2021-05-01"
            :max="today"
          />
        </template>
        <!-- 고객 조회 -->
        <template v-if="searchMode === 'customer'">
          <div class="info-label">차량번호</div>
          <input
            type="text"
            v-model="searchKeyword"
            placeholder="ex) 12*3456"
            class="search-input"
            @input="filterCarNumber"
          />
        </template>
        <button class="search-btn" @click="fetchList">조회</button>
      </div>
    </div>
    <div class="main">
      <!-- AG Grid 테이블 -->
      <ag-grid-vue
        class="ag-cr-balham"
        :columnDefs="columnDefs"
        :rowData="rowData"
        :gridOptions="gridOptions"
        :theme="theme"
        @grid-ready="onGridReady"
        />
        <!-- @cell-double-clicked="onCellDoubleClicked" -->
    </div>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue3";
import axios from "axios";
import "ag-grid-community/styles/ag-grid.css";
import { themeBalham } from "ag-grid-community";

export default {
  name: "CrListPage",
  components: {
    AgGridVue,
  },
  data() {
    return {
      userInfo: null, // 로그인한 사용자 정보

      today: (() => {
        const d = new Date();
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      })(),
      searchMode: "receipt", // 'receipt' 또는 'customer'

      selectedBranch: "", // 선택된 본부
      branchList: [],
      selectedTeam: "", // 선택된 팀
      teamList: [], // 팀 목록
      teamSearch: "", // 매장 검색용

      theme: themeBalham.withParams({
        fontFamily: "Malgun Gothic",
        // rowHeight: 16,
        borderColor: "#808080",
        wrapperBorder: true,
        headerRowBorder: true,
        headerColumnBorderHeight: "100%",
        rowBorder: { style: "solid", width: 1 },
        columnBorder: { style: "solid", width: 1 },
      }),

      selectedDate: "00",
      searchDate: (() => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      })(),
      searchKeyword: "",
      gridApi: null,
      columnDefs: [
        {
          headerName: "",
          valueGetter: "node.rowIndex + 1",
          width: 25,
          cellStyle: { textAlign: "center", backgroundColor: "#f0f0f0" },
        },
        {
          headerName: "",
          field: "ID",
          hide: true, // 숨김 컬럼
        },
        {
          field: "SALE_DATE",
          headerName: "날짜",
          flex: 0.9,
          cellStyle: this.gridCellStyle,
        },
        {
          field: "BILL_NO",
          headerName: "영수번호",
          flex: 0.5,
          cellStyle: this.gridCellStyle,
        },
        {
          field: "CST_NO",
          headerName: "고객번호",
          flex: 0.6,
          cellStyle: this.gridCellStyle,
        },
        {
          field: "CST_NM",
          headerName: "고객명",
          flex: 0.7,
          cellStyle: this.gridCellStyle,
        },
        {
          field: "DCM_SALE_AMT",
          headerName: "구매금액",
          flex: 0.8,
          cellStyle: this.gridCellStyle,
          valueFormatter: (params) => {
            return Number(params.value).toLocaleString();
          },
        },
        {
          field: "SALE_ITEM",
          headerName: "구매상품",
          flex: 1.3,
          cellStyle: this.gridCellStyle,
        },
        {
          field: "CAR_NO",
          headerName: "차량번호",
          flex: 0.6,
          cellStyle: this.gridCellStyle,
        },
        {
          field: "CAR_MODEL_NM",
          headerName: "차종",
          flex: 1,
          cellStyle: this.gridCellStyle,
        },
        {
          field: "L_ACC_KM",
          headerName: "주행거리",
          flex: 0.8,
          cellStyle: this.gridCellStyle,
          valueFormatter: (params) => {
            return Number(params.value).toLocaleString();
          },
        },
        {
          field: "SEND_CMT",
          headerName: "상태",
          flex: 0.6,
          cellStyle: this.gridCellStyle,
        },
      ],
      gridOptions: {
        headerHeight: 35,
        rowHeight: 25,
        suppressHorizontalScroll: false,
        suppressDragLeaveHidesColumns: true, // 드래그 후 열 숨기기 방지
        suppressNoRowsOverlay: true,
        defaultColDef: {
          sortable: true,
          comparator: (valueA, valueB) => {
            const isANumber = !isNaN(parseFloat(valueA)) && isFinite(valueA);
            const isBNumber = !isNaN(parseFloat(valueB)) && isFinite(valueB);

            if (isANumber && isBNumber) {
              return valueA - valueB;
            }
            if (!isANumber && !isBNumber) {
              return String(valueA).localeCompare(String(valueB));
            }
            return isANumber ? -1 : 1;
          },
          resizable: true,
          headerStyle: {
            color: "black",
            textAlign: "center",
            alignContent: "center",
          },
        },
      },
      rowData: [],
    };
  },
  watch: {
    selectedBranch(newBranchCd) {
      // 본부가 선택되면 팀 목록을 불러옵니다.
      if (newBranchCd) {
        this.fetchTeamList(newBranchCd).then(() => {
          // 사용자가 직접 본부를 변경했을 때만 매장 선택을 초기화
          if (this.userInfo.OGN_FG === "00") {
            this.selectedTeam = "";
          }
        });
      } else {
        this.teamList = [];
        this.selectedTeam = "";
      }
    },
    selectedTeam(newShopCd) {
      if (newShopCd && newShopCd !== "00") {
        const team = this.teamList.find((t) => t.SHOP_CD === newShopCd);
        if (team) {
          const teamName = `(${team.VAN_BIZ_NO}) ${team.SHOP_NM}`;
          if (this.teamSearch !== teamName) {
            this.teamSearch = teamName;
          }
        }
        this.fetchList();
      } else {
        if (this.teamSearch !== "") {
          this.teamSearch = "";
        }
      }
    },
  },
  methods: {
    async fetchCenterList() {
      try {
        const res = await axios.get("/api/crListPage/centerList");
        this.branchList = res.data;
      } catch (error) {
        console.error("본부 목록 로딩 실패:", error);
        alert("본부 목록을 불러오는 데 실패했습니다.");
      }
    },
    async fetchTeamList(branchCd) {
      try {
        const res = await axios.get(
          `/api/crListPage/teamList?branchCd=${branchCd}`
        );
        this.teamList = res.data;
      } catch (error) {
        console.error("팀 목록 로딩 실패:", error);
        this.teamList = [];
      }
    },
    onGridReady(params) {
      this.gridApi = params.api;
      // this.fetchList();
    },
    async fetchList() {
      try {
        const params = {
          searchMode: this.searchMode,
        };

        if (this.searchMode === "receipt") {
          if (!this.selectedTeam) {
            alert("매장 선택해주세요.");
            return;
          }
          if (!this.searchDate) {
            alert("날짜를 선택해주세요.");
            return;
          }
          params.shopCd = this.selectedTeam;
          params.searchDate = this.searchDate;
        } else {
          if (!this.searchKeyword) {
            alert("차량번호를 입력해주세요.");
            return;
          }
          params.carNumber = this.searchKeyword;
        }
        const response = await axios.get("/api/crListPage/fetchList", {
          params,
        });

        // AG Grid 데이터 업데이트
        if (response.data && Array.isArray(response.data)) {
          this.rowData = response.data;
        } else {
          this.rowData = [];
          alert("조회된 데이터가 없습니다.");
        }
      } catch (error) {
        console.error("데이터 조회 실패:", error);
        alert("데이터 조회 중 오류가 발생했습니다.");
      }
    },
    filterCarNumber(event) {
      // 숫자와 '*'만 허용합니다.
      const filteredValue = event.target.value.replace(/[^0-9*]/g, "");
      this.searchKeyword = filteredValue;
    },

    handleTeamSelection() {
      const team = this.teamList.find(
        (t) => `(${t.VAN_BIZ_NO}) ${t.SHOP_NM}` === this.teamSearch
      );
      if (team) {
        if (this.selectedTeam !== team.SHOP_CD) {
          this.selectedTeam = team.SHOP_CD;
        }
      } else {
        this.selectedTeam = "";
      }
    },

    onCellDoubleClicked(params) {
      const { data } = params;
      if (data && data.SALE_DATE && data.ID) {
        // SALE_DATE가 'YYYY-MM-DD ...' 형식이므로 앞의 10자리를 추출하고 '-'를 제거합니다.
        const formattedDate = data.SALE_DATE.substring(0, 10).replace(/-/g, "");
        // 요청하신 형식에 맞게 URL을 생성합니다.
        const url = `http://pic.tirebank.com/mmsimg/RC${formattedDate}/${data.ID}.jpg`;
        // 새 탭에서 URL을 엽니다.
        window.open(url, "_blank");
      }
    },

    gridCellStyle(params) {
      const style = {};
      const field = params.colDef.field;
      const centerAlignFields = [
        "SALE_DATE",
        "BILL_NO",
        "CST_NO",
        "CST_NM",
        "CAR_NO",
      ];

      if (centerAlignFields.includes(field)) {
        style.textAlign = "center";
      } else if (field == "DCM_SALE_AMT" || field == "L_ACC_KM") {
        style.textAlign = "right";
      } else {
        style.textAlign = "left";
      }

      return style;
    },
    validateDate(event) {
      const min = event.target.min;
      const max = event.target.max;
      const value = event.target.value;

      if (value) {
        if (value < min) {
          this.searchDate = min;
        } else if (value > max) {
          this.searchDate = max;
        }
      }
    },
    async setInitialSelection() {
      // 본부 목록을 먼저
      await this.fetchCenterList();

      if (this.userInfo) {
        if (this.userInfo.OGN_FG === "00") {
          // 본사 또는 관리자: '전체'를 기본값으로 설정합니다.
          if (this.branchList.length > 0) {
            this.selectedBranch = this.branchList[0].BRANCH_CD;
          }
        } else if (this.userInfo.OGN_FG === "01") {
          // 본부 또는 매장: 사용자의 본부와 매장을 기본값으로 설정
          this.selectedBranch = this.userInfo.BRANCH_CD;
          await this.fetchTeamList(this.selectedBranch);
          this.selectedTeam = this.userInfo.SHOP_CD;
        }
      } else if (this.branchList.length > 0) {
        this.selectedBranch = this.branchList[0].BRANCH_CD;
      }
    },
  },
  mounted() {
    const userInfoString = localStorage.getItem("userInfo");
    if (userInfoString) {
      this.userInfo = JSON.parse(userInfoString);
    }

    this.setInitialSelection();
  },
};
</script>

<style scoped>
.cr-list-page {
  padding: 10px 5px;
}

.search-area {
  margin-bottom: 10px;
  padding: 10px;
  background-color: #f5f5f5;
}

.search-row {
  display: flex;
  /* gap: 10px; */
  justify-content: center; /* 시작점에서 정렬합니다. */
  width: 100%;
}

.search-row > * {
  font-family: Arial, sans-serif;
  font-size: 0.9rem;
}

.date-picker {
  padding: 10px;
  border: 1px solid;
  border-radius: 4px;
}

.search-btn {
  padding: 0;
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  width: 10%;
}

.info-label {
  padding: 6px 12px;
  border: 1px solid #ccc;
  background-color: #ffc080;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
}

.branch-select {
  padding: 10px;
  border-radius: 4px;
  border: 1px solid;
  width: 120px; /* 너비 고정 */
}

.team-select {
  padding: 10px;
  border-radius: 4px;
  border: 1px solid;
  width: 160px; /* 너비 고정 */
}

.search-input {
  border-radius: 4px;
  border: 1px solid;
}

.search-mode-select {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-right: 10px;
}

:deep(.ag-cr-balham) {
  height: calc(100vh - 180px);
  --ag-cell-horizontal-padding: 3px; /* 셀 내부 좌우 패딩 */
}

:deep(.ag-header-cell) {
  background-color: #f0f0f0;
}

:deep(.ag-header-cell-label) {
  justify-content: center;
}

:deep(.ag-cell) {
  font-size: 0.8rem;
}

/* 1024px 초과일 때 폰트/행 높이 증가 */
@media (min-width: 1025px) {
  :deep(.ag-cr-balham .ag-cell) {
    font-size: 1rem;
  }
  .search-row > * {
    font-size: 1rem;
  }
}
</style>
